import React, { useEffect, useMemo, useState } from "react";
import {
    ArrowLeft, Plus, Play, Square, Trash2, KeyRound, ShieldAlert, Grid2x2, RefreshCw, X, ExternalLink,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import {
    fetchGrids, createGrid, deleteGrid, startGrid, stopGrid, setGridMode,
    setGridKill, setGridCredentials, clearGridCredentials, fetchSymbols,
} from "@/lib/api";
import { ConfirmLiveDialog } from "./ConfirmDialogs";

const C = {
    bg: "#0B0F14", panel: "#0F1620", border: "#1B2A3A", text: "#E6EDF6",
    sub: "#A7B4C6", dim: "#6F7D92", accent: "#2EE6D6", bull: "#2BE38B",
    warn: "#FFB020", bear: "#FF5C6C", info: "#3DA7FF",
};

const inputCls = "w-full bg-[#0B0F14] border rounded px-2 py-1.5 text-xs font-mono focus:outline-none";
const errText = (e) => e?.response?.data?.detail || e.message;

/* ---------------- Token dialog ---------------- */
const TokenDialog = ({ grid, onClose, onDone }) => {
    const [signer, setSigner] = useState("");
    const [pk, setPk] = useState("");
    const [busy, setBusy] = useState(false);
    if (!grid) return null;
    const save = async () => {
        if (!signer.trim() || !pk.trim()) { toast.error("Signer address and private key are required"); return; }
        setBusy(true);
        try {
            await setGridCredentials(grid.gid, signer.trim(), pk.trim());
            toast.success(`${grid.symbol} now uses its own token`);
            onDone();
        } catch (e) { toast.error(`Save failed: ${errText(e)}`); }
        finally { setBusy(false); }
    };
    const clear = async () => {
        setBusy(true);
        try {
            await clearGridCredentials(grid.gid);
            toast.success(`${grid.symbol} reverted to the shared wallet`);
            onDone();
        } catch (e) { toast.error(`Clear failed: ${errText(e)}`); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }} onClick={onClose}>
            <div className="panel w-full max-w-md p-5 max-h-[90dvh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="grid-token-dialog">
                <div className="flex items-center gap-2 mb-1">
                    <KeyRound size={15} color={C.accent} />
                    <div className="text-sm font-semibold">{grid.symbol} · Grid Token</div>
                    <button className="ml-auto" onClick={onClose}><X size={16} color={C.dim} /></button>
                </div>
                <div className="text-[11px] mb-3" style={{ color: C.dim }}>
                    Give this grid its own AsterDex wallet. Leave the primary grid untouched.
                    The grid must be stopped. The key is stored server-side and never shown again.
                </div>
                <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: C.dim }}>Signer / wallet address</label>
                <input className={inputCls} style={{ borderColor: C.border, color: C.text }} value={signer}
                    onChange={(e) => setSigner(e.target.value)} placeholder="0x…" data-testid="grid-token-signer" />
                <label className="text-[10px] uppercase tracking-[0.14em] block mb-1 mt-3" style={{ color: C.dim }}>Private key</label>
                <input className={inputCls} style={{ borderColor: C.border, color: C.text }} type="password" value={pk}
                    onChange={(e) => setPk(e.target.value)} placeholder="0x… (kept secret)" data-testid="grid-token-pk" />
                {grid.running && (
                    <div className="mt-3 text-[11px] flex items-center gap-1.5" style={{ color: C.warn }}>
                        <ShieldAlert size={12} /> Stop the grid before changing its token.
                    </div>
                )}
                <div className="flex items-center gap-2 mt-4">
                    <button disabled={busy || grid.running} onClick={save}
                        className="btn-anim h-8 px-3 rounded text-xs font-medium disabled:opacity-40"
                        style={{ background: C.accent, color: C.bg }} data-testid="grid-token-save">Save token</button>
                    {grid.own_token && (
                        <button disabled={busy || grid.running} onClick={clear}
                            className="btn-anim h-8 px-3 rounded text-xs disabled:opacity-40"
                            style={{ border: `1px solid ${C.border}`, color: C.sub }}>Use shared wallet</button>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ---------------- Create form ---------------- */
const CreateGrid = ({ symbols, onCreated }) => {
    const [open, setOpen] = useState(false);
    const [f, setF] = useState({ symbol: "", grid_size: "", buy_qty: "", max_buys: "10", inventory_cap: "", usdt_reserve: "", signer_address: "", private_key: "" });
    const [busy, setBusy] = useState(false);
    const [showToken, setShowToken] = useState(false);

    const submit = async () => {
        if (!f.symbol || !f.grid_size || !f.buy_qty) { toast.error("Symbol, grid size and buy qty are required"); return; }
        setBusy(true);
        try {
            const payload = {
                symbol: f.symbol.toUpperCase(), grid_size: Number(f.grid_size),
                buy_qty: Number(f.buy_qty), max_buys: Number(f.max_buys) || 10,
            };
            if (f.inventory_cap) payload.inventory_cap = Number(f.inventory_cap);
            if (f.usdt_reserve !== "") payload.usdt_reserve = Number(f.usdt_reserve);
            if (f.signer_address && f.private_key) { payload.signer_address = f.signer_address.trim(); payload.private_key = f.private_key.trim(); }
            await createGrid(payload);
            toast.success(`Grid created for ${payload.symbol} (paper)`, { description: "Review settings, then Start." });
            setF({ symbol: "", grid_size: "", buy_qty: "", max_buys: "10", inventory_cap: "", usdt_reserve: "", signer_address: "", private_key: "" });
            setOpen(false); setShowToken(false);
            onCreated();
        } catch (e) { toast.error(`Create failed: ${errText(e)}`); }
        finally { setBusy(false); }
    };

    if (!open) {
        return (
            <button onClick={() => setOpen(true)} className="btn-anim flex items-center gap-1.5 h-9 px-3 rounded-md text-[12px] font-medium"
                style={{ background: "rgba(46,230,214,0.14)", border: `1px solid ${C.accent}55`, color: C.accent }} data-testid="grid-create-open">
                <Plus size={14} /> New Grid
            </button>
        );
    }
    return (
        <div className="panel p-4" data-testid="grid-create-form">
            <div className="flex items-center gap-2 mb-3">
                <Grid2x2 size={14} color={C.accent} />
                <span className="text-[11px] uppercase tracking-[0.16em]" style={{ color: C.sub }}>New Grid</span>
                <button className="ml-auto" onClick={() => setOpen(false)}><X size={15} color={C.dim} /></button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                    <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: C.dim }}>Pair</label>
                    <input list="grid-symbols" className={inputCls} style={{ borderColor: C.border, color: C.text }} value={f.symbol}
                        onChange={(e) => setF({ ...f, symbol: e.target.value })} placeholder="ETHUSDT" data-testid="grid-create-symbol" />
                    <datalist id="grid-symbols">
                        {symbols.map((s) => <option key={s.symbol} value={s.symbol} />)}
                    </datalist>
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: C.dim }}>Grid size ($)</label>
                    <input className={inputCls} style={{ borderColor: C.border, color: C.text }} type="number" value={f.grid_size}
                        onChange={(e) => setF({ ...f, grid_size: e.target.value })} placeholder="10" data-testid="grid-create-gridsize" />
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: C.dim }}>Buy qty (base)</label>
                    <input className={inputCls} style={{ borderColor: C.border, color: C.text }} type="number" value={f.buy_qty}
                        onChange={(e) => setF({ ...f, buy_qty: e.target.value })} placeholder="0.05" data-testid="grid-create-buyqty" />
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: C.dim }}>Max buys</label>
                    <input className={inputCls} style={{ borderColor: C.border, color: C.text }} type="number" value={f.max_buys}
                        onChange={(e) => setF({ ...f, max_buys: e.target.value })} data-testid="grid-create-maxbuys" />
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: C.dim }}>Inventory cap (base)</label>
                    <input className={inputCls} style={{ borderColor: C.border, color: C.text }} type="number" value={f.inventory_cap}
                        onChange={(e) => setF({ ...f, inventory_cap: e.target.value })}
                        placeholder="auto: qty × maxBuys × 1.5" data-testid="grid-create-invcap" />
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: C.dim }}>USDT reserve</label>
                    <input className={inputCls} style={{ borderColor: C.border, color: C.text }} type="number" value={f.usdt_reserve}
                        onChange={(e) => setF({ ...f, usdt_reserve: e.target.value })}
                        placeholder="100" data-testid="grid-create-reserve" />
                </div>
            </div>
            <button onClick={() => setShowToken((v) => !v)} className="mt-3 text-[11px] flex items-center gap-1.5" style={{ color: C.sub }}>
                <KeyRound size={12} /> {showToken ? "Hide" : "Set a dedicated token (optional)"}
            </button>
            {showToken && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                    <input className={inputCls} style={{ borderColor: C.border, color: C.text }} value={f.signer_address}
                        onChange={(e) => setF({ ...f, signer_address: e.target.value })} placeholder="Signer address 0x…" />
                    <input className={inputCls} style={{ borderColor: C.border, color: C.text }} type="password" value={f.private_key}
                        onChange={(e) => setF({ ...f, private_key: e.target.value })} placeholder="Private key (optional)" />
                </div>
            )}
            <div className="flex items-center gap-2 mt-4">
                <button disabled={busy} onClick={submit} className="btn-anim h-8 px-3 rounded text-xs font-medium disabled:opacity-40"
                    style={{ background: C.accent, color: C.bg }} data-testid="grid-create-submit">{busy ? "Creating…" : "Create grid (paper)"}</button>
                <span className="text-[10px]" style={{ color: C.dim }}>New grids start in PAPER — go live per-grid after review.</span>
            </div>
        </div>
    );
};

/* ---------------- Grid card ---------------- */
const GridCard = ({ g, onChanged, onToken }) => {
    const [busy, setBusy] = useState(false);
    const [liveOpen, setLiveOpen] = useState(false);
    const wrap = async (fn, okMsg) => {
        setBusy(true);
        try { await fn(); if (okMsg) toast.success(okMsg); onChanged(); }
        catch (e) { toast.error(errText(e)); }
        finally { setBusy(false); }
    };
    // Same OTP ceremony as the main bot: the backend now requires a verified
    // live-grant for grid:{gid}, so a bare confirm() is no longer enough.
    const goLive = () => {
        if (!g.creds_present) { toast.error("Set a valid token before going live"); return; }
        setLiveOpen(true);
    };
    const del = async () => {
        if (!window.confirm(`Delete the ${g.symbol} grid? Open BUYs are cancelled first.`)) return;
        await wrap(() => deleteGrid(g.gid), `${g.symbol} grid deleted`);
    };
    const live = g.mode === "live";
    return (
        <div className="panel p-4" data-testid={`grid-card-${g.gid}`}>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="font-mono text-sm font-semibold" style={{ color: C.text }}>{g.symbol}</span>
                <span className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-[0.1em] font-mono"
                    style={g.running ? { background: "rgba(43,227,139,0.12)", color: C.bull } : { background: "#13202D", color: C.dim }}>
                    {g.running ? "running" : "stopped"}
                </span>
                <span className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-[0.1em] font-mono"
                    style={live ? { background: "rgba(255,92,108,0.12)", color: C.bear } : { background: "rgba(46,230,214,0.1)", color: C.accent }}>
                    {live ? "LIVE" : "PAPER"}
                </span>
                <span className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-[0.1em] font-mono"
                    style={{ background: "#13202D", color: g.own_token ? C.info : C.dim }} title={g.signer_address || ""}>
                    {g.own_token ? "own token" : "shared wallet"}
                </span>
                <a href={`#/grids/${g.gid}`}
                    className="btn-anim ml-auto flex items-center gap-1 px-2 py-1 rounded text-[10px]"
                    style={{ border: `1px solid ${C.accent}45`, color: C.accent }}
                    data-testid={`grid-open-${g.gid}`}>
                    <ExternalLink size={11} /> Open
                </a>
                <span className="text-[10px] font-mono" style={{ color: C.dim }}>#{g.gid}</span>
            </div>

            <div className="grid grid-cols-4 gap-2 mb-3 text-center">
                {[["Price", g.last_price != null ? `$${Number(g.last_price).toLocaleString("en-US", { maximumFractionDigits: 2 })}` : "—"],
                  ["Grid", `$${g.grid_size}`],
                  ["Buys/Sells", `${g.active_buys}/${g.active_sells}`],
                  ["PnL", `$${Number(g.realized_pnl || 0).toFixed(2)}`]].map(([l, v], i) => (
                    <div key={l} className="rounded p-1.5" style={{ background: "#0B1219" }}>
                        <div className="text-[9px] uppercase tracking-[0.1em]" style={{ color: C.dim }}>{l}</div>
                        <div className="font-mono text-[12px]" style={{ color: i === 3 ? (g.realized_pnl >= 0 ? C.bull : C.bear) : C.text }}>{v}</div>
                    </div>
                ))}
            </div>
            {g.last_error && <div className="text-[10px] mb-2 font-mono truncate" style={{ color: C.bear }} title={g.last_error}>err: {g.last_error}</div>}

            <div className="flex items-center gap-2 flex-wrap">
                {!g.running ? (
                    <button disabled={busy} onClick={() => wrap(() => startGrid(g.gid, g.mode), `${g.symbol} started`)}
                        className="btn-anim flex items-center gap-1 h-8 px-2.5 rounded text-xs disabled:opacity-40"
                        style={{ background: C.bull, color: C.bg }} data-testid={`grid-start-${g.gid}`}><Play size={12} /> Start</button>
                ) : (
                    <button disabled={busy} onClick={() => wrap(() => stopGrid(g.gid), `${g.symbol} stopped`)}
                        className="btn-anim flex items-center gap-1 h-8 px-2.5 rounded text-xs disabled:opacity-40"
                        style={{ background: C.bear, color: C.bg }} data-testid={`grid-stop-${g.gid}`}><Square size={12} /> Stop</button>
                )}
                {live ? (
                    <button disabled={busy} onClick={() => wrap(() => setGridMode(g.gid, "paper"), `${g.symbol} → PAPER`)}
                        className="btn-anim h-8 px-2.5 rounded text-xs disabled:opacity-40" style={{ border: `1px solid ${C.border}`, color: C.accent }}>To Paper</button>
                ) : (
                    <button disabled={busy} onClick={goLive}
                        className="btn-anim h-8 px-2.5 rounded text-xs disabled:opacity-40" style={{ border: `1px solid ${C.warn}55`, color: C.warn }} data-testid={`grid-live-${g.gid}`}>Go Live</button>
                )}
                <button disabled={busy} onClick={() => wrap(() => setGridKill(g.gid, !g.kill_switch), null)}
                    className="btn-anim h-8 px-2.5 rounded text-xs disabled:opacity-40"
                    style={{ border: `1px solid ${C.border}`, color: g.kill_switch ? C.bear : C.dim }}>{g.kill_switch ? "Kill: ON" : "Kill"}</button>
                <button disabled={busy} onClick={() => onToken(g)}
                    className="btn-anim flex items-center gap-1 h-8 px-2.5 rounded text-xs disabled:opacity-40"
                    style={{ border: `1px solid ${C.border}`, color: C.sub }} data-testid={`grid-token-${g.gid}`}><KeyRound size={12} /> Token</button>
                <button disabled={busy} onClick={del}
                    className="btn-anim flex items-center gap-1 h-8 px-2.5 rounded text-xs ml-auto disabled:opacity-40"
                    style={{ border: `1px solid ${C.border}`, color: C.bear }} data-testid={`grid-delete-${g.gid}`}><Trash2 size={12} /></button>
            </div>
            <ConfirmLiveDialog
                open={liveOpen}
                onOpenChange={setLiveOpen}
                bot={`grid:${g.gid}`}
                credsPresent={g.creds_present}
                useTestnet={g.use_testnet}
                onConfirm={async () => {
                    setLiveOpen(false);
                    await wrap(() => setGridMode(g.gid, "live"), `${g.symbol} → LIVE`);
                }}
            />
        </div>
    );
};

export default function MultiGridPage({ onBack }) {
    const [data, setData] = useState(null);
    const [symbols, setSymbols] = useState([]);
    const [tokenGrid, setTokenGrid] = useState(null);

    const load = async () => {
        try { setData(await fetchGrids()); } catch (e) { toast.error(`Load failed: ${errText(e)}`); }
    };
    useEffect(() => { load(); const id = setInterval(load, 2500); return () => clearInterval(id); }, []);
    useEffect(() => { fetchSymbols().then((r) => setSymbols(r.items || [])).catch(() => {}); }, []);

    const grids = useMemo(() => data?.items || [], [data?.items]);
    const primary = data?.primary;
    const full = useMemo(
        () => grids.length >= (data?.max_grids || 12),
        [grids, data?.max_grids],
    );

    return (
        <div className="min-h-screen" style={{ background: C.bg, color: C.text }} data-testid="multigrid-page">
            <Toaster position="bottom-right" toastOptions={{ style: { background: C.panel, border: `1px solid ${C.border}`, color: C.text } }} />
            <div className="sticky top-0 z-40 backdrop-blur-md" style={{ background: "rgba(11,15,20,0.85)", borderBottom: `1px solid ${C.border}` }}>
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-3 flex items-center gap-3 flex-wrap">
                    <button onClick={onBack} className="btn-anim flex items-center gap-1.5 text-sm" style={{ color: C.sub }} data-testid="multigrid-back">
                        <ArrowLeft size={16} /> Dashboard
                    </button>
                    <div className="h-6 w-px" style={{ background: C.border }} />
                    <Grid2x2 size={16} color={C.accent} />
                    <span className="text-sm font-semibold">Multi-Grid · Concurrent Pairs</span>
                    {primary && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ color: C.dim, background: "#13202D" }}>primary: {primary.symbol}</span>}
                    <div className="flex-1" />
                    <button onClick={load} className="btn-anim flex items-center gap-1 text-[11px]" style={{ color: C.sub }}><RefreshCw size={12} /> refresh</button>
                </div>
            </div>

            <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-5 space-y-4">
                <div className="flex items-start gap-2 text-[11px] rounded-md p-3" style={{ background: "rgba(255,176,32,0.07)", border: `1px solid rgba(255,176,32,0.3)`, color: C.warn }}>
                    <ShieldAlert size={14} className="mt-0.5 shrink-0" />
                    <span>All grids share one machine and (unless given their own token) one wallet. New grids start in PAPER. Going LIVE places real orders — set a dedicated token per grid to isolate funds, and mind total capital across grids.</span>
                </div>

                {full ? (
                    <div className="panel p-3 text-[11px]" style={{ color: C.dim }}>
                        Maximum of {data?.max_grids} grids reached — delete one to add another.
                    </div>
                ) : (
                    <div><CreateGrid symbols={symbols} onCreated={load} /></div>
                )}

                {grids.length === 0 && (
                    <div className="panel p-8 text-center text-sm" style={{ color: C.dim }} data-testid="multigrid-empty">
                        No additional grids yet. Use “New Grid” to run a second pair alongside {primary?.symbol || "the primary"}.
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {grids.map((g) => <GridCard key={g.gid} g={g} onChanged={load} onToken={setTokenGrid} />)}
                </div>
            </div>

            {tokenGrid && <TokenDialog grid={grids.find((x) => x.gid === tokenGrid.gid) || tokenGrid} onClose={() => setTokenGrid(null)} onDone={() => { setTokenGrid(null); load(); }} />}
        </div>
    );
}
